/**
 * Reports — the workbook library (Lane 1132, Plan A chat workbooks, WS7).
 *
 * A dense, artifact-led list: one hairline row per LOGICAL report (latest
 * version first), expandable immutable version history, authenticated
 * Download, and a mode-aware Update action ("Update with current data"
 * for rolling recipes, "Refresh this period" for fixed ones). Creation is
 * 100% conversational — this page is only where built files live (D6).
 *
 * Read-only rollback: when the creation gate is OFF but artifacts exist,
 * the list and downloads stay available and the Update action hides
 * (server 404s it independently — double lock).
 *
 * Pagination is an explicit visible "Load more" button (never infinite
 * scroll); after a page appends, focus moves to the first new row.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './Reports.linear.css';

function windowLabel(row) {
  if (row.window_mode === 'rolling') {
    const w = (row.window || '').replace(/_/g, ' ');
    return w ? `Rolling ${w}` : 'Rolling window';
  }
  if (row.window_mode === 'fixed') {
    return row.window ? `Fixed ${row.window}` : 'Fixed window';
  }
  return row.window || '';
}

function updateLabel(row) {
  return row.window_mode === 'fixed'
    ? 'Refresh this period' : 'Update with current data';
}

function jobBadge(job) {
  if (!job) return null;
  if (job.status === 'failed') {
    return job.next_action === 'retry'
      ? 'Last run failed. Retry available'
      : 'Last run failed. Revise in chat';
  }
  const phase = {
    queued: 'Queued', running: 'Running', validating: 'Validating',
    publishing: 'Publishing',
  }[job.status];
  if (!phase) return null;
  const who = job.is_initiator ? '' : ` by ${job.initiator_name || 'a teammate'}`;
  return `${phase}${who}…`;
}

export default function Reports() {
  const { authFetch, workbookExportEnabled } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [state, setState] = useState('loading'); // loading|ready|error
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // report_id
  const [notice, setNotice] = useState('');
  // Focus contract (WS7): after Load more appends a page, focus moves to
  // the first NEWLY appended row. lastPageStart is the index where the
  // most recent page began.
  const [lastPageStart, setLastPageStart] = useState(-1);
  const firstNewRowRef = useRef(null);

  const fetchPage = useCallback(async (cursor) => {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const response = await authFetch(`/api/reports${qs}`);
    if (!response.ok) throw new Error(`list ${response.status}`);
    return response.json();
  }, [authFetch]);
  const activeJobKey = items
    .filter((row) => row.series_job
      && ['queued', 'running', 'validating', 'publishing']
        .includes(row.series_job.status))
    .map((row) => `${row.report_id}:${row.series_job.job_id}`)
    .sort().join('|');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await fetchPage(null);
        if (cancelled) return;
        setItems(page.items);
        setNextCursor(page.next_cursor);
        setState('ready');
      } catch (e) {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPage]);

  // Jobs are durable server state. Keep rows moving through queued -> ready
  // without requiring a page reload; a terminal transition refreshes the
  // report list so the newly published immutable version replaces the row.
  useEffect(() => {
    const active = items.filter((row) => row.series_job
      && ['queued', 'running', 'validating', 'publishing']
        .includes(row.series_job.status));
    if (active.length === 0) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      let terminal = false;
      await Promise.all(active.map(async (row) => {
        try {
          const response = await authFetch(
            `/api/report-jobs/${row.series_job.job_id}`);
          if (!response.ok) return;
          const job = await response.json();
          if (cancelled) return;
          terminal = terminal || ['ready', 'failed', 'cancelled']
            .includes(job.status);
          setItems((prev) => prev.map((item) => item.report_id === row.report_id
            ? { ...item, series_job: job } : item));
        } catch (e) { /* keep last-known state and poll again */ }
      }));
      if (cancelled) return;
      if (terminal) {
        try {
          const page = await fetchPage(null);
          if (!cancelled) {
            setItems(page.items);
            setNextCursor(page.next_cursor);
          }
        } catch (e) { /* next poll/list visit recovers */ }
      }
      timer = setTimeout(poll,
        document.visibilityState === 'hidden' ? 5000 : 2000);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [authFetch, fetchPage, activeJobKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    setLoadingMore(true);
    const before = items.length;
    try {
      const page = await fetchPage(nextCursor);
      setItems((prev) => prev.concat(page.items));
      setNextCursor(page.next_cursor);
      setLastPageStart(before);
      setTimeout(() => {
        if (firstNewRowRef.current) firstNewRowRef.current.focus();
      }, 0);
    } catch (e) {
      setNotice('Could not load more reports. Try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const download = async (reportId, filename) => {
    setNotice('');
    try {
      const response = await authFetch(`/api/reports/${reportId}/download`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setNotice(body?.detail?.message
          || 'The file could not be downloaded right now.');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'report.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice('The file could not be downloaded right now.');
    }
  };

  const runUpdate = async (row) => {
    setBusyAction(row.report_id);
    setNotice('');
    try {
      const key = `upd-${row.report_id}-${Date.now()}`;
      const response = await authFetch(
        `/api/reports/${row.report_id}/update`,
        { method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idempotency_key: key }) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNotice(body?.detail?.message
          || 'The update could not be started.');
        return;
      }
      // Reflect the queued job on the row immediately.
      setItems((prev) => prev.map((r) => (
        r.report_id === row.report_id
          ? { ...r,
              series_job: { status: 'queued', is_initiator: true,
                            job_id: body.job_id } }
          : r)));
    } catch (e) {
      setNotice('The update could not be started.');
    } finally {
      setBusyAction(null);
    }
  };

  const retryJob = async (row) => {
    const jobId = row.series_job?.job_id;
    if (!jobId) return;
    setBusyAction(row.report_id);
    setNotice('');
    try {
      const response = await authFetch(`/api/report-jobs/${jobId}/retry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: `retry-${jobId}-${Date.now()}` }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNotice(body?.detail?.message || 'The retry could not be started.');
        return;
      }
      setItems((prev) => prev.map((item) => item.report_id === row.report_id
        ? { ...item, series_job: { ...item.series_job,
          job_id: body.job_id, status: 'queued', is_initiator: true } }
        : item));
    } catch (e) {
      setNotice('The retry could not be started.');
    } finally {
      setBusyAction(null);
    }
  };

  const loadVersions = async (row, cursor = null) => {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const response = await authFetch(
      `/api/reports/${row.report_id}/versions${qs}`);
    if (!response.ok) return null;
    return response.json();
  };

  return (
    <div className="rpts">
      <div className="rpts-wrap">
        <div className="rpts-head">
          <div>
            <h1>Reports</h1>
            <div className="sub">
              Workbooks built from chat. Every version stays downloadable.
            </div>
          </div>
        </div>

        <div aria-live="polite" className="rpts-notice">
          {notice}
        </div>

        {state === 'loading' && (
          <div className="rpts-empty" aria-busy="true">Loading reports…</div>
        )}

        {state === 'error' && (
          <div className="rpts-empty">
            <p>Reports could not be loaded.</p>
            <button className="rpts-link" type="button"
              onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        )}

        {state === 'ready' && items.length === 0 && (
          <div className="rpts-empty">
            <p>No workbooks yet. Workbooks you build from chat will appear
              here.</p>
            <button className="rpts-link" type="button"
              onClick={() => navigate('/')}>
              Ask Wingman for a report
            </button>
          </div>
        )}

        {state === 'ready' && items.length > 0 && (
          <ul className="rpts-list">
            {items.map((row, index) => (
              <ReportRow
                key={row.report_id}
                row={row}
                rowRef={index === lastPageStart ? firstNewRowRef : null}
                canUpdate={workbookExportEnabled}
                busy={busyAction === row.report_id}
                onDownload={download}
                onUpdate={runUpdate}
                onRetry={retryJob}
                onRevise={() => navigate('/')}
                onLoadVersions={loadVersions}
              />
            ))}
          </ul>
        )}

        {state === 'ready' && nextCursor && (
          <button className="rpts-more" type="button" onClick={loadMore}
            disabled={loadingMore} aria-busy={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}

function ReportRow({ row, rowRef, canUpdate, busy, onDownload, onUpdate,
                     onRetry, onRevise, onLoadVersions }) {
  const [versions, setVersions] = useState(null);
  const [versionCursor, setVersionCursor] = useState(null);
  const [versionsBusy, setVersionsBusy] = useState(false);
  const badge = jobBadge(row.series_job);
  const jobActive = row.series_job && ['queued', 'running', 'validating',
    'publishing'].includes(row.series_job.status);
  const anotherUserActive = jobActive && !row.series_job.is_initiator;

  const onToggleVersions = async (event) => {
    if (event.target.open && versions === null) {
      const page = await onLoadVersions(row);
      setVersions(page ? page.items : []);
      setVersionCursor(page ? page.next_cursor : null);
    }
  };

  const loadMoreVersions = async () => {
    setVersionsBusy(true);
    try {
      const page = await onLoadVersions(row, versionCursor);
      if (page) {
        setVersions((current) => (current || []).concat(page.items));
        setVersionCursor(page.next_cursor);
      }
    } finally { setVersionsBusy(false); }
  };

  return (
    <li className="rpts-row">
      <div className="rpts-row-main">
        <div className="rpts-identity">
          <button
            type="button"
            ref={rowRef}
            className="rpts-title"
            title={row.title}
            onClick={() => onDownload(row.report_id, row.filename)}
          >
            {row.title}
          </button>
          <span className="rpts-meta">
            {windowLabel(row)}
            {row.as_of ? ` · as of ${row.as_of}` : ''}
            {` · v${row.version}`}
            {row.created_by_name ? ` · ${row.created_by_name}` : ''}
          </span>
          {badge && (
            <span className={'rpts-badge'
              + (row.series_job.status === 'failed' ? ' is-failed' : '')}>
              {badge}
            </span>
          )}
        </div>
        <div className="rpts-actions">
          <button
            className="rpts-btn rpts-btn-primary"
            type="button"
            onClick={() => onDownload(row.report_id, row.filename)}
          >
            Download
          </button>
          {canUpdate && row.series_job?.status !== 'failed' && (
            <button
              className="rpts-btn"
              type="button"
              disabled={busy || jobActive}
              aria-busy={busy}
              title={anotherUserActive
                ? `Update in progress by ${row.series_job.initiator_name
                    || 'a teammate'}`
                : undefined}
              onClick={() => onUpdate(row)}
            >
              {jobActive ? 'Update in progress…' : updateLabel(row)}
            </button>
          )}
          {canUpdate && row.series_job?.status === 'failed'
            && row.series_job.can_retry && (
            <button className="rpts-btn" type="button" disabled={busy}
              onClick={() => onRetry(row)}>Retry</button>
          )}
          {row.series_job?.status === 'failed'
            && row.series_job.next_action === 'revise' && (
            <button className="rpts-btn" type="button"
              onClick={onRevise}>Revise in chat</button>
          )}
        </div>
      </div>
      <details className="rpts-versions" onToggle={onToggleVersions}>
        <summary>Version history</summary>
        {versions === null && <div className="rpts-vrow">Loading…</div>}
        {Array.isArray(versions) && versions.map((version) => (
          <div className="rpts-vrow" key={version.report_id}>
            <span>
              v{version.version}
              {version.report_id === row.report_id ? ' (current)' : ''}
              {' · '}{version.created_at
                ? String(version.created_at).slice(0, 10) : ''}
            </span>
            <button className="rpts-link" type="button"
              onClick={() => onDownload(version.report_id,
                version.filename)}>
              Download
            </button>
          </div>
        ))}
        {versionCursor && (
          <button className="rpts-link" type="button"
            disabled={versionsBusy} onClick={loadMoreVersions}>
            {versionsBusy ? 'Loading…' : 'Load more versions'}
          </button>
        )}
      </details>
    </li>
  );
}
